"use client";

import { useMemo, useState } from "react";
import {
  LuNewspaper,
  LuExternalLink,
  LuChevronDown,
  LuChevronUp,
  LuCalendar,
  LuSearch,
  LuX,
} from "react-icons/lu";
import Image from "next/image";
import newsData from "@/json/countryNews.json";

const NewsPage = () => {
  const [expandedCountries, setExpandedCountries] = useState<
    Record<string, boolean>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const toggleCountry = (country: string) => {
    setExpandedCountries((prev) => ({
      ...prev,
      [country]: !prev[country],
    }));
  };

  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return newsData
      .filter((c) => !selectedCountry || c.country === selectedCountry)
      .map((c) => ({
        ...c,
        articles: q
          ? c.articles.filter(
              (a) =>
                a.title.toLowerCase().includes(q) ||
                a.description.toLowerCase().includes(q),
            )
          : c.articles,
      }))
      .filter((c) => c.articles.length > 0);
  }, [searchQuery, selectedCountry]);

  const totalArticles = newsData.reduce((sum, c) => sum + c.articles.length, 0);

  return (
    <main className="min-h-screen">
      {/* Header Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <h1 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-3">
            <LuNewspaper size={36} className="text-indigo-500" />
            Global Policy News
          </h1>
          <p className="mt-4 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Stay informed on the latest immigration, education, and economic
            policy updates across {newsData.length} countries with{" "}
            {totalArticles} curated articles.
          </p>
        </div>
      </section>

      {/* Search + Filter bar */}
      <section className="max-w-6xl mx-auto px-5 pb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <LuX className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="sm:w-52 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Countries</option>
            {newsData.map((c) => (
              <option key={c.country} value={c.country}>
                {c.country}
              </option>
            ))}
          </select>
        </div>
        {(searchQuery || selectedCountry) && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {filteredData.reduce((s, c) => s + c.articles.length, 0)} result
            {filteredData.reduce((s, c) => s + c.articles.length, 0) !== 1
              ? "s"
              : ""}{" "}
            found
          </p>
        )}
      </section>

      {/* News Grid */}
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="space-y-6">
          {filteredData.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              No articles match your search.
            </div>
          ) : null}
          {filteredData.map((countryData) => {
            const isExpanded = expandedCountries[countryData.country] ?? true;
            const articleCount = countryData.articles.length;

            return (
              <article
                key={countryData.country}
                className="rounded-[22px] bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px] overflow-hidden"
              >
                {/* Country Header */}
                <button
                  onClick={() => toggleCountry(countryData.country)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-200/50 dark:hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <Image
                      src={`https://flagcdn.com/w40/${countryData.flagCode}.png`}
                      alt={`${countryData.country} flag`}
                      width={40}
                      height={28}
                      className="w-10 h-7 rounded object-cover border border-gray-300 dark:border-gray-600"
                    />
                    <div className="text-left">
                      <h2 className="text-xl md:text-2xl font-bold">
                        {countryData.country}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {articleCount}{" "}
                        {articleCount === 1 ? "article" : "articles"}
                      </p>
                    </div>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {isExpanded ? (
                      <LuChevronUp size={24} />
                    ) : (
                      <LuChevronDown size={24} />
                    )}
                  </div>
                </button>

                {/* Articles */}
                {isExpanded && (
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {countryData.articles.map((article, idx) => (
                        <div
                          key={idx}
                          className="group relative rounded-xl p-5 bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer"
                        >
                          <h3 className="font-semibold text-[15px] leading-snug mb-2 line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-3">
                            {article.description}
                          </p>
                          <div className="flex items-center justify-between">
                            {article.url && (
                              <a
                                href={article.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                              >
                                Read more
                                <LuExternalLink size={14} />
                              </a>
                            )}
                            {article.date && (
                              <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <LuCalendar size={12} />
                                {article.date}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

    </main>
  );
};

export default NewsPage;
